import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PixelAvatar } from '../../utils/avatarGenerator';
import './PlayerDashboard.css';

// Check if we're in Telegram environment
const isTelegramWebApp = typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp;

const PlayerDashboard = ({ wallet, onCheckIn, lastCheckIn, nextCheckInTime }) => {
  const [playerStatus, setPlayerStatus] = useState({
    isAlive: true,
    survivalStreak: 1,
  });

  const [timeLeft, setTimeLeft] = useState({
    hours: 24,
    minutes: 0,
    seconds: 0,
  });

  const [gameStats, setGameStats] = useState({
    activePlayers: 15,
    deadPlayers: 5,
    totalPlayers: 20,
    prizePool: 42,
    dailyFee: 0.1,
  });

  const [players, setPlayers] = useState([]);
  const [mapSize] = useState({ width: 20, height: 15 });
  const playersInitialized = useRef(false);

  // Generate initial players - stabilized with useCallback and ref to prevent regeneration
  const generatePlayers = useCallback(() => {
    console.log('generatePlayers called, initialized:', playersInitialized.current);
    if (playersInitialized.current) return [];
    
    const playerList = [];
    
    // Add the current player
    playerList.push({
      id: 'current-player',
      address: wallet?.address || 'current-player',
      x: Math.floor(mapSize.width / 2),
      y: Math.floor(mapSize.height / 2),
      isCurrentPlayer: true,
      isAlive: true,
      direction: { x: 0, y: 0 },
      lastMove: Date.now() - 3000, // Allow immediate movement
    });

    // Add other active players
    for (let i = 0; i < 14; i++) { // Fixed number instead of gameStats dependency
      playerList.push({
        id: `player-${i}`,
        address: `bot-player-${i}`,
        x: Math.floor(Math.random() * mapSize.width),
        y: Math.floor(Math.random() * mapSize.height),
        isCurrentPlayer: false,
        isAlive: true,
        direction: {
          x: Math.random() > 0.5 ? 1 : -1,
          y: Math.random() > 0.5 ? 1 : -1,
        },
        lastMove: Date.now() - Math.random() * 4000, // Stagger initial movement times
      });
    }

    playersInitialized.current = true;
    console.log('Generated players:', playerList.length);
    return playerList;
  }, [wallet?.address, mapSize]);

  // Move players randomly to adjacent squares
  const movePlayer = useCallback((player) => {
    const now = Date.now();
    
    // Move every 2-5 seconds (randomized for more natural movement)
    const moveInterval = 2000 + Math.random() * 3000;
    if (now - player.lastMove < moveInterval) {
      return player;
    }

    // Get all adjacent squares including diagonals
    const adjacentSquares = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const newX = player.x + dx;
        const newY = player.y + dy;
        
        // Make sure the new position is within bounds
        if (newX >= 0 && newX < mapSize.width && newY >= 0 && newY < mapSize.height) {
          adjacentSquares.push({ x: newX, y: newY });
        }
      }
    }

    // Pick a random adjacent square
    const randomSquare = adjacentSquares[Math.floor(Math.random() * adjacentSquares.length)];

    console.log(`Moving player ${player.id} from (${player.x},${player.y}) to (${randomSquare.x},${randomSquare.y})`);

    return {
      ...player,
      x: randomSquare.x,
      y: randomSquare.y,
      lastMove: now,
    };
  }, [mapSize]);

  const updateCountdown = useCallback(() => {
    if (!nextCheckInTime) return;

    const now = new Date();
    const diff = nextCheckInTime - now;

    if (diff <= 0) {
      setPlayerStatus(prev => ({ ...prev, isAlive: false }));
      // Simulate player elimination
      setGameStats(prev => ({
        ...prev,
        activePlayers: Math.max(1, prev.activePlayers - 1),
        deadPlayers: prev.deadPlayers + 1,
      }));
      return;
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    setTimeLeft({ hours, minutes, seconds });
  }, [nextCheckInTime]);

  // Initialize players once
  useEffect(() => {
    if (!playersInitialized.current) {
      const initialPlayers = generatePlayers();
      console.log('Initializing players:', initialPlayers?.length);
      if (initialPlayers && initialPlayers.length > 0) {
        setPlayers(initialPlayers);
      }
    }
  }, [generatePlayers]);

  // Move players animation - improved with better error handling
  useEffect(() => {
    console.log('Setting up player movement interval');
    const moveInterval = setInterval(() => {
      setPlayers(currentPlayers => {
        if (!currentPlayers || currentPlayers.length === 0) {
          console.log('No players to move');
          return currentPlayers;
        }
        
        const movedPlayers = currentPlayers
          .filter(player => player.isAlive) // Only keep alive players
          .map(movePlayer);
          
        console.log('Moving players:', movedPlayers.length);
        return movedPlayers;
      });
    }, 1000); // Check every second for smoother movement

    return () => {
      console.log('Cleaning up movement interval');
      clearInterval(moveInterval);
    };
  }, [movePlayer]);

  // Update game stats periodically
  useEffect(() => {
    const statsInterval = setInterval(() => {
      setGameStats(prev => {
        // Randomly eliminate players over time (small chance)
        if (Math.random() < 0.02 && prev.activePlayers > 1) {
          return {
            ...prev,
            activePlayers: prev.activePlayers - 1,
            deadPlayers: prev.deadPlayers + 1,
            prizePool: prev.prizePool + 0.1,
          };
        }
        return prev;
      });
    }, 10000); // Every 10 seconds

    return () => clearInterval(statsInterval);
  }, []);

  useEffect(() => {
    // Initialize the app
    if (isTelegramWebApp) {
      window.Telegram.WebApp.ready();
      window.Telegram.WebApp.expand();
    }

    // Start countdown timer
    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [updateCountdown]);

  const handleCheckIn = async () => {
    try {
      await onCheckIn();
      setPlayerStatus(prev => ({ 
        ...prev, 
        survivalStreak: prev.survivalStreak + 1 
      }));
    } catch (error) {
      console.error('Check-in failed:', error);
      if (isTelegramWebApp) {
        window.Telegram.WebApp.showAlert('Failed to check in. Please try again.');
      } else {
        alert('Failed to check in. Please try again.');
      }
    }
  };

  return (
    <div className="player-dashboard">
      <div className="status-header">
        <div className={`status-indicator ${playerStatus.isAlive ? 'alive' : 'eliminated'}`}>
          {playerStatus.isAlive ? '💚 ALIVE' : '☠️ ELIMINATED'}
        </div>
        <div className="wallet-info">
          {wallet?.address?.slice(0, 6)}...{wallet?.address?.slice(-4)}
        </div>
      </div>

      <div className="countdown-section">
        <h2>Time Until Next Check-in</h2>
        <div className="countdown-timer">
          {String(timeLeft.hours).padStart(2, '0')}:
          {String(timeLeft.minutes).padStart(2, '0')}:
          {String(timeLeft.seconds).padStart(2, '0')}
        </div>
      </div>

      {/* Live Game Map */}
      <div className="game-map-section">
        <h3>Live Game Map</h3>
        <div className="stats-bar">
          <span className="stat">👥 {gameStats.activePlayers} alive</span>
          <span className="stat">☠️ {gameStats.deadPlayers} eliminated</span>
          <span className="stat">💰 {gameStats.prizePool.toFixed(1)} TON</span>
        </div>
        
        {/* Debug info */}
        <div style={{ fontSize: '10px', color: '#888', marginBottom: '10px' }}>
          Players: {players.length} | Map: {mapSize.width}x{mapSize.height}
          {players.length > 0 && (
            <div>
              Current Player: ({players.find(p => p.isCurrentPlayer)?.x}, {players.find(p => p.isCurrentPlayer)?.y})
            </div>
          )}
          <div style={{ marginTop: '5px' }}>
            <button 
              onClick={() => {
                playersInitialized.current = false;
                const newPlayers = generatePlayers();
                console.log('Manual generation:', newPlayers);
                setPlayers(newPlayers);
              }}
              style={{ fontSize: '10px', padding: '2px 5px', marginRight: '5px' }}
            >
              Regenerate Players
            </button>
            <button 
              onClick={() => {
                setPlayers(currentPlayers => currentPlayers.map(movePlayer));
              }}
              style={{ fontSize: '10px', padding: '2px 5px' }}
            >
              Force Move All
            </button>
          </div>
        </div>
        
        <div className="game-map">
          {Array.from({ length: mapSize.height }, (_, y) => (
            <div key={y} className="map-row">
              {Array.from({ length: mapSize.width }, (_, x) => {
                const playersOnCell = players.filter(p => p.x === x && p.y === y);
                const currentPlayerOnCell = playersOnCell.find(p => p.isCurrentPlayer);
                const otherPlayerOnCell = playersOnCell.find(p => !p.isCurrentPlayer);
                
                return (
                  <div key={x} className="map-cell" style={{ 
                    backgroundColor: playersOnCell.length > 0 ? 'rgba(255,255,0,0.2)' : 'rgba(0,50,0,0.3)' 
                  }}>
                    {currentPlayerOnCell && (
                      <div className="player-avatar current-player">
                        <PixelAvatar 
                          address={currentPlayerOnCell.address} 
                          size={16} 
                        />
                      </div>
                    )}
                    {otherPlayerOnCell && !currentPlayerOnCell && (
                      <div className="player-avatar">
                        <PixelAvatar 
                          address={otherPlayerOnCell.address} 
                          size={16} 
                        />
                      </div>
                    )}
                    {playersOnCell.length > 0 && (
                      <div style={{ 
                        position: 'absolute', 
                        top: '0', 
                        right: '0', 
                        fontSize: '8px', 
                        color: 'white',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        padding: '1px'
                      }}>
                        {playersOnCell.length}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="check-in-section">
        <button 
          className={`check-in-btn ${!playerStatus.isAlive ? 'disabled' : ''}`}
          onClick={handleCheckIn}
          disabled={!playerStatus.isAlive}
        >
          Pay Daily Tax (0.1 TON)
        </button>
        <p className="check-in-hint">
          Last check-in: {lastCheckIn ? lastCheckIn.toLocaleString() : 'Never'}
        </p>
        <p className="survival-streak">
          Survival Streak: {playerStatus.survivalStreak} days
        </p>
      </div>

      <div className="game-stats">
        <div className="stat-card">
          <h3>Prize Pool</h3>
          <p>{gameStats.prizePool.toFixed(1)} TON</p>
        </div>
        <div className="stat-card">
          <h3>Players Left</h3>
          <p>{gameStats.activePlayers}</p>
        </div>
        <div className="stat-card">
          <h3>Daily Fee</h3>
          <p>{gameStats.dailyFee} TON</p>
        </div>
      </div>
    </div>
  );
};

export default PlayerDashboard; 